import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

/**
 * Full-bleed background for the parallax band. Matches Duda Homestead: a single
 * static image (featured image by default), not a gallery carousel.
 */
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Tour anytime, from any distance',
    },
  },
};

/** Primary call-to-action. */
const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Schedule a Visit',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

/** Secondary call-to-action. */
const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/virtual-tour',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Take a Virtual Tour',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function ParallaxBanner() {
  const bg = useSlotGroup('parallaxbanner.bg_image', bgImageDef);
  const primaryCta = useSlotGroup('parallaxbanner.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('parallaxbanner.secondary_cta', secondaryCtaDef);

  return (
    <section className="parallax-band relative w-full bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6">
      {/* Inset frame that clips the background to the band. Side gutters match
          the hero's so both boxes line up on the same vertical edges. */}
      <div className="parallax-box relative w-full overflow-hidden">
        <div className="absolute inset-0 z-0">
          <EditableSlotGroup groupPath="parallaxbanner.bg_image" definition={bgImageDef}>
            <div
              className="parallax-layer absolute inset-0"
              role="img"
              aria-label={bg.alt || ''}
              style={{
                backgroundImage: bg.image ? `url("${bg.image}")` : undefined,
              }}
            />
          </EditableSlotGroup>
        </div>

        {/* Neutral dark overlay for legibility over the desaturated photo. */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
        />

        {/* Centered content. */}
        <div className="relative z-10 flex min-h-[clamp(260px,36vh,400px)] flex-col items-center justify-center px-6 py-10 text-center text-white">
          <RichTextContent
            path="parallaxbanner.eyebrow"
            label="Eyebrow"
            defaultValue="<p>Tour Anytime. From Any Distance.</p>"
            as="p"
            className="parallax-eyebrow text-lg sm:text-xl md:text-2xl font-normal uppercase drop-shadow-md"
          />
          <RichTextContent
            path="parallaxbanner.title"
            label="Title"
            defaultValue="<p>We&rsquo;re Here For You</p>"
            as="h2"
            className="parallax-title mt-1 text-5xl md:text-6xl lg:text-7xl font-normal leading-none tracking-tight drop-shadow-lg"
          />

          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:gap-5">
            <EditableSlotGroup groupPath="parallaxbanner.primary_cta" definition={primaryCtaDef}>
              <a
                href={primaryCta.url || '/schedule-a-tour'}
                target={primaryCta._target || '_self'}
                className="parallax-cta inline-flex items-center justify-center min-w-[15rem] whitespace-nowrap px-10 py-5 text-sm transition-all duration-300"
              >
                {primaryCta.label || 'Schedule a Visit'}
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="parallaxbanner.secondary_cta" definition={secondaryCtaDef}>
              <a
                href={secondaryCta.url || '/virtual-tour'}
                target={secondaryCta._target || '_self'}
                className="parallax-cta inline-flex items-center justify-center min-w-[15rem] whitespace-nowrap px-10 py-5 text-sm transition-all duration-300"
              >
                {secondaryCta.label || 'Take a Virtual Tour'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .parallax-layer {
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          background-attachment: scroll;
        }
        /* Pin the background from the tablet breakpoint up so it stays
           stationary against the viewport while the box scrolls over it.
           Phones keep the plain scrolling cover image. */
        @media (min-width: 768px) {
          .parallax-layer {
            background-attachment: fixed;
          }
        }
        /* On narrow screens cap the measure so the title breaks onto a second
           line. Width-based rather than a hard-coded <br> so it still works
           after the title is edited. */
        @media (max-width: 639px) {
          .parallax-title {
            max-width: min(11ch, 100%);
            margin-inline: auto;
            text-wrap: balance;
          }
          /* Keep the eyebrow to a single line; the viewport-scaled size keeps
             the default copy inside the band's inner width (100vw minus 72px
             of gutters) from 320px up. */
          .parallax-eyebrow,
          .parallax-eyebrow p {
            white-space: nowrap;
            font-size: clamp(0.625rem, 3.4vw, 1.125rem);
          }
        }
        .parallax-cta {
          background: #000;
          color: #fff;
          border: 1px solid #000;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          letter-spacing: 0.03em;
        }
        .parallax-cta:hover {
          background: #fff;
          color: #000;
          transform: translateY(-2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .parallax-cta:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ParallaxBanner',
  displayName: 'Parallax Banner',
  tokens: {
    'parallaxbanner.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Tour Anytime. From Any Distance.</p>',
    },
    'parallaxbanner.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>We&rsquo;re Here For You</p>',
    },
  },
  slotGroups: {
    'parallaxbanner.bg_image': bgImageDef,
    'parallaxbanner.primary_cta': primaryCtaDef,
    'parallaxbanner.secondary_cta': secondaryCtaDef,
  },
};

(ParallaxBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ParallaxBanner;
